import React from "react";
import { CurrencyRupeeIcon } from "@heroicons/react/outline";
import MetalPolicyCard from "../components/MetalPolicyCard";

const HybridTab = ({
  rateConfig,
  setRateConfig,
  updateHybridMetalPolicy,
  branches,
}) => {
  const metals = [
    { id: "gold", name: "Gold", color: "yellow", icon: "yellow-600" },
    { id: "silver", name: "Silver", color: "gray", icon: "gray-500" },
    { id: "platinum", name: "Platinum", color: "purple", icon: "purple-600" },
    { id: "diamond", name: "Diamond", color: "blue", icon: "blue-600" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3">Hybrid Policy Settings</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure which metals should inherit rates and which should use
          custom rates
        </p>

        {rateConfig.inheritFromBranch && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Metals set to "Inherit" will use rates from{" "}
              <span className="font-semibold">
                {branches?.find((b) => b._id === rateConfig.inheritFromBranch)
                  ?.name || "selected branch"}
              </span>
            </p>
          </div>
        )}

        {/* Hybrid Mode Options */}
        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <h4 className="font-medium mb-3">Hybrid Configuration</h4>
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={rateConfig.hybridConfig.inheritBase}
                onChange={(e) =>
                  setRateConfig((prev) => ({
                    ...prev,
                    hybridConfig: {
                      ...prev.hybridConfig,
                      inheritBase: e.target.checked,
                    },
                  }))
                }
                className="h-4 w-4 text-indigo-600 rounded mr-3"
              />
              <span>Inherit base rates from main branch</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={rateConfig.hybridConfig.customizeMargins}
                onChange={(e) =>
                  setRateConfig((prev) => ({
                    ...prev,
                    hybridConfig: {
                      ...prev.hybridConfig,
                      customizeMargins: e.target.checked,
                    },
                  }))
                }
                className="h-4 w-4 text-indigo-600 rounded mr-3"
              />
              <span>Apply custom margins on inherited rates</span>
            </label>
          </div>

          <div className="mt-4 text-sm bg-purple-100 p-3 rounded">
            <p className="font-medium">Current Setting:</p>
            <p className="mt-1">
              {rateConfig.hybridConfig.inheritBase
                ? "Using base rates from main branch"
                : "Using custom base rates"}{" "}
              {rateConfig.hybridConfig.customizeMargins
                ? "with custom margins"
                : "with inherited margins"}
            </p>
          </div>
        </div>

        {/* Metal Specific Policies */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {metals.map((metal) => (
            <MetalPolicyCard
              key={metal.id}
              metal={metal}
              policy={rateConfig.hybridConfig.metalPolicies[metal.id]}
              onPolicyChange={(policy) =>
                updateHybridMetalPolicy(metal.id, policy)
              }
            />
          ))}
        </div>

        {/* Summary Card */}
        <HybridSummaryCard
          metalPolicies={rateConfig.hybridConfig.metalPolicies}
          inheritBase={rateConfig.hybridConfig.inheritBase}
          customizeMargins={rateConfig.hybridConfig.customizeMargins}
        />
      </div>
    </div>
  );
};

const HybridSummaryCard = ({
  metalPolicies,
  inheritBase,
  customizeMargins,
}) => (
  <div className="mt-6 bg-indigo-50 p-4 rounded-lg">
    <h4 className="font-medium mb-2">Hybrid Policy Summary</h4>
    <ul className="space-y-1 text-sm">
      {Object.entries(metalPolicies).map(([metal, policy]) => (
        <li key={metal} className="flex justify-between">
          <span className="capitalize">{metal}:</span>
          <span className="font-medium">
            {policy === "INHERIT" ? "Inherit" : "Custom"}
          </span>
        </li>
      ))}
      <li className="border-t pt-1 mt-1 flex justify-between">
        <span>Base Rate Source:</span>
        <span className="font-medium">
          {inheritBase ? "Main Branch" : "Custom"}
        </span>
      </li>
      <li className="flex justify-between">
        <span>Margins:</span>
        <span className="font-medium">
          {customizeMargins ? "Custom" : "Inherited"}
        </span>
      </li>
    </ul>
  </div>
);

export default HybridTab;
